import { destroyProducts, useExportProductsXlsx, useProducts } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Chip, CircularProgress, Paper, TextField, Typography } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import type { PermissionTuple } from '~/utils/ability'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { useToast } from '../../../components/communication/Toast'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import ImportExportIcon from '../../../components/icons/ImportExportIcon'
import PlusIcon from '../../../components/icons/PlusIcon'
import Page from '../../../components/Page'
import { downloadBlob } from '../../../utils/excel/downloadBlob'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

/**
 * La liste est le seul point d'entrée des écrans produit : un rôle qui n'a que `add`, `edit` ou
 * `import` voit l'entrée de menu (union des descendants) et doit pouvoir l'ouvrir — chaque colonne
 * et chaque bouton restant gardés un à un (audit du 19/09, MAJ-07).
 */
const PRODUCTS_PAGE_READ = moduleIndexPermission([
  ['read', 'products.products.product'],
  ['add', 'products.products.product'],
  ['edit', 'products.products.product'],
  ['import', 'products.products.product'],
])

export const Route = createFileRoute('/_authenticated/products/')({
  staticData: {
    permission: PRODUCTS_PAGE_READ,
  },
  component: ProductsPage,
})
withRoutePermissions(Route)

const TAB_WIDTH = 170
const SMALL_TAB = 100
const MEDIUM_WIDTH = 200
const BIGGER_TAB = 230

const productColDefs: GridColDef[] = [
  {
    field: 'reference',
    headerName: 'EAN 13',
    width: TAB_WIDTH,
  },
  {
    field: 'dun14',
    headerName: 'DUN 14',
    width: TAB_WIDTH,
  },
  { field: 'label', headerName: 'Libellé', width: BIGGER_TAB },
  {
    field: 'internalCategory',
    headerName: 'Catégorie interne',
    renderCell: (field) => <Chip label={field.value} />,
    width: MEDIUM_WIDTH,
  },
  {
    field: 'categories',
    headerName: 'Catégories Commerciales',
    renderCell: (field) =>
      field.value?.map((cat: string) => (
        <Chip key={`category-${cat}`} label={cat} sx={{ mx: '2px' }} />
      )),
    width: BIGGER_TAB,
  },
  { field: 'brand', headerName: 'Marque', width: TAB_WIDTH },
  {
    field: 'isNew',
    headerName: 'Nouveauté',
    renderCell: (field) => (field.value === 'oui' ? <Chip label={field.value} /> : null),
    width: SMALL_TAB,
  },
  {
    field: 'isActive',
    headerName: 'Activé',
    renderCell: (field) => (field.value === 'oui' ? <Chip label={field.value} /> : null),
    width: SMALL_TAB,
  },
  {
    field: 'antiGaspi',
    headerName: 'Anti-Gaspi',
    width: SMALL_TAB,
  },
  {
    field: 'copilot',
    headerName: 'Copilot',
    width: TAB_WIDTH,
  },
]

const EDIT_PRODUCT: PermissionTuple = ['edit', 'products.products.product']
const READ_PRODUCT: PermissionTuple = ['read', 'products.products.product']

function ProductsPage() {
  const navigate = Route.useNavigate()
  const products = useProducts()
  const [Toast, showToast] = useToast()

  const { mutate: exportProductsXlsx, isPending: isExportingProductsXlsx } = useExportProductsXlsx()

  // ****************************************************** //
  //                    PRODUCT VARIABLES                   //
  // ****************************************************** //

  const [productSearch, setProductSearch] = useState('')
  const [productsSelection, setproductsSelection] = useState<number[]>([])

  // ****************************************************** //
  //           QUERY TO DATAGRID TRANSFORMATION             //
  // ****************************************************** //

  // Transform fetched products into DataGrid format
  const productRows = useMemo(() => {
    if (products.data === undefined) {
      return []
    }
    return products.data
      .filter((product) => product.isActive)
      .filter((product) => includesLowerCase(product.label, productSearch))
      .map((product) => ({
        id: product.id, // Datagrid requires a unique id field
        reference: product.reference,
        dun14: product.dun14,
        label: product.label,
        internalCategory: product.internalCategory.name,
        categories: product.categories.map((c) => c.name),
        brand: product.brand.name,
        isNew: product.isNew ? 'oui' : 'non',
        isActive: product.isActive ? 'oui' : 'non',
        antiGaspi: `J-${product.antiGaspi}`,
        copilot: product.copilotCode,
      }))
  }, [products.data, productSearch])

  // ****************************************************** //
  //                         MUTATIONS                      //
  // ****************************************************** //

  const queryClient = useQueryClient()

  const deleteProductMut = useMutation({
    mutationFn: destroyProducts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setproductsSelection([])
    },
  })

  // ****************************************************** //
  //                         HANDLERS                       //
  // ****************************************************** //

  const navigateAndUpdateProduct = async () => {
    // Navigate to first step with current selected id
    navigate({
      params: { productId: productsSelection[0] },
      to: '/products/$productId/edit',
    })
  }

  const navigateToProductDetails = async () => {
    // Navigate to first step with current selected id
    navigate({
      params: { productId: productsSelection[0] },
      to: '/products/$productId',
    })
  }

  const handleExportProductsXlsx = () => {
    exportProductsXlsx(undefined, {
      onError: (error) => {
        showToast({
          severity: 'error',
          message: error.message,
        })
      },
      onSuccess: (data) => {
        downloadBlob({ blob: data, filename: 'products.xlsx' })
      },
    })
  }

  const productsDisabled = useMemo(
    () => productsSelection.length === 0 || deleteProductMut.isPending,
    [productsSelection, deleteProductMut.isPending],
  )

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Produits
        </Typography>

        <AuthorizedButtonLink
          variant="outlined"
          color="secondary"
          sx={{
            marginRight: '16px',
            backgroundColor: 'white',
            ...ButtonOverrides.shadow,
          }}
          to="/products/new"
          legacyRights={['ADMIN']}
        >
          <PlusIcon orange />
          Ajouter un produit
        </AuthorizedButtonLink>
        <AuthorizedButton
          variant="contained"
          color="secondary"
          sx={{
            marginRight: '16px',
          }}
          onClick={handleExportProductsXlsx}
          permission={['export', 'products.products.product']}
          legacyRights={['ADMIN']}
        >
          <ImportExportIcon />
          Exporter
          {isExportingProductsXlsx && <CircularProgress size={20} />}
        </AuthorizedButton>
        <AuthorizedButtonLink
          variant="contained"
          color="secondary"
          sx={{
            marginRight: '16px',
          }}
          to="/products/import"
          legacyRights={['ADMIN']}
        >
          <ImportExportIcon />
          Importer
        </AuthorizedButtonLink>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: '16px', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />
          <AuthorizedButton
            variant="contained"
            sx={{ height: '36px' }}
            disabled={productsDisabled}
            onClick={navigateAndUpdateProduct}
            permission={EDIT_PRODUCT}
            legacyRights={['ADMIN']}
          >
            Modifier
          </AuthorizedButton>
          <AuthorizedButton
            variant="contained"
            color="secondary"
            sx={{ height: '36px' }}
            disabled={productsDisabled}
            onClick={navigateToProductDetails}
            permission={READ_PRODUCT}
          >
            Voir
          </AuthorizedButton>
        </div>
        <SimpleDataGrid
          columns={productColDefs}
          rows={productRows}
          loading={products.isLoading || deleteProductMut.isPending}
          onRowSelectionModelChange={(newSelection) =>
            setproductsSelection(newSelection as number[])
          }
        />
      </Paper>
    </Page>
  )
}
